/**
 * MessagesPage — full-page chat experience (Tier 1).
 *
 * Three-column layout on desktop:
 *   Left:   Conversation sidebar + contact requests tab
 *   Center: Active chat window
 *   Right:  (reserved for future info panel / participants)
 *
 * On narrow viewports the columns stack.
 *
 * Route: /messages  and  /messages/:conversationId
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  fetchConversations,
  fetchConversation,
  fetchPendingRequests,
  fetchSentRequests,
  acceptContact,
  rejectContact,
  archiveConversation,
  createConversation,
  pinConversation,
} from '../../api/chat';
import { fetchAllSecurityUsers } from '../../api/security';
import type { ConversationResponse, ContactResponse } from '../../api/chat';
import type { SecurityUserResponse } from '../../api/security';
import { onChatEvent, ChatHubEvents, joinConversationGroup } from '../../api/chatConnection';
import ChatSidebar from '../../Components/Chat/ChatSidebar';
import ChatWindow from '../../Components/Chat/ChatWindow';
import NewConversationPanel from '../../Components/Chat/NewConversationPanel';
import ContactRequestsPanel from '../../Components/Chat/ContactRequestsPanel';
import styles from './MessagesPage.module.css';

type SidebarTab = 'conversations' | 'requests';

interface MessagesPageProps {
  currentUserGuid: string;
}

export default function MessagesPage({ currentUserGuid }: MessagesPageProps) {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useAppTranslation('chat');

  /* ---- Data ---- */
  const [conversations, setConversations] = useState<ConversationResponse[]>([]);
  const [activeConversation, setActiveConversation] = useState<ConversationResponse | null>(null);
  const [platformUsers, setPlatformUsers] = useState<SecurityUserResponse[]>([]);
  const [pendingRequests, setPendingRequests] = useState<ContactResponse[]>([]);
  /* DEV-1485 — requests I SENT, awaiting the other person's answer. Previously
     unfetched, which is why a sent request appeared nowhere. */
  const [sentRequests, setSentRequests] = useState<ContactResponse[]>([]);
  const [loading, setLoading] = useState(true);

  /* ---- Enrich conversation participants with correct user data ---- */
  // The backend may return stale or incorrect participant profileImageUrl/displayName
  // (e.g. returning the authenticated user's data for all participants).
  // Fix: patch each participant's profile data from the authoritative platformUsers lookup.
  const enrichedConversations = useMemo(() => {
    if (platformUsers.length === 0) return conversations;
    const userMap = new Map<string, SecurityUserResponse>();
    for (const u of platformUsers) {
      userMap.set(u.userGuid.toUpperCase(), u);
    }
    return conversations.map((conv) => ({
      ...conv,
      participants: conv.participants.map((p) => {
        const realUser = userMap.get(p.userGuid.toUpperCase());
        if (!realUser) return p;
        // Always prefer realUser data — do NOT fall back to p.profileImageUrl
        // because the backend may return the authenticated user's avatar for all participants.
        // An empty string means "no avatar" (show initials), which is correct.
        return {
          ...p,
          displayName: realUser.displayName || realUser.name || p.displayName,
          screenName: realUser.screenName || p.screenName,
          profileImageUrl: realUser.profileImageUrl,
        };
      }),
    }));
  }, [conversations, platformUsers]);

  /* ---- UI state ---- */
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('conversations');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  /* ---- Initial data load ---- */
  // Each fetch is independent so one failure doesn't block the others
  // (e.g. chat API down should not prevent user search from working).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const loadConversations = fetchConversations()
      .then((convs) => { if (!cancelled) setConversations(convs); })
      .catch((err) => console.error('[MessagesPage] Failed to load conversations:', err));

    const loadUsers = fetchAllSecurityUsers()
      .then((users) => { if (!cancelled) setPlatformUsers(users); })
      .catch((err) => console.error('[MessagesPage] Failed to load users:', err));

    const loadRequests = fetchPendingRequests()
      .then((reqs) => {
        if (cancelled) return;
        const inbound = reqs.filter(
          (r) => r.recipientGuid.toUpperCase() === currentUserGuid.toUpperCase()
        );
        setPendingRequests(inbound);
      })
      .catch((err) => console.error('[MessagesPage] Failed to load requests:', err));

    // DEV-1485 — outgoing requests. Separate endpoint (/contacts/sent) because
    // /contacts/pending is recipient-scoped by design.
    const loadSent = fetchSentRequests()
      .then((reqs) => { if (!cancelled) setSentRequests(reqs); })
      .catch((err) => console.error('[MessagesPage] Failed to load sent requests:', err));

    Promise.allSettled([loadConversations, loadUsers, loadRequests, loadSent])
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [currentUserGuid]);

  /* ---- Load conversation from URL param ---- */
  useEffect(() => {
    if (!conversationId) {
      setActiveConversation(null);
      return;
    }
    fetchConversation(conversationId)
      .then(setActiveConversation)
      .catch((err) => {
        console.error('[MessagesPage] Failed to load conversation:', err);
        setActiveConversation(null);
      });
  }, [conversationId]);

  /* ---- Real-time: refresh conversation list on new messages / new convos / read receipts ---- */
  useEffect(() => {
    const refreshConvs = () => {
      fetchConversations()
        .then(setConversations)
        .catch(() => { /* silent */ });
    };
    const unsub1 = onChatEvent(ChatHubEvents.NewMessage, refreshConvs);
    const unsub2 = onChatEvent(ChatHubEvents.ConversationInvite, (data: any) => {
      // Join the SignalR group for the new conversation so we receive messages
      if (data?.conversationId) {
        joinConversationGroup(data.conversationId).catch(() => {});
      }
      refreshConvs();
    });
    // Refresh when read receipts arrive so unread counts update in the sidebar
    const unsub3 = onChatEvent(ChatHubEvents.ReadReceipt, refreshConvs);
    // Also refresh when ChatWindow dispatches a local badge-change event
    const handleBadgeChange = () => refreshConvs();
    window.addEventListener('l4e_badges_changed', handleBadgeChange);
    return () => {
      unsub1(); unsub2(); unsub3();
      window.removeEventListener('l4e_badges_changed', handleBadgeChange);
    };
  }, []);

  /* ---- Handlers ---- */
  const handleSelectConversation = useCallback(
    (id: string) => {
      navigate(`/messages/${id}`);
      setShowNewConversation(false);
    },
    [navigate]
  );

  const handleConversationCreated = useCallback(
    (conv: ConversationResponse) => {
      setConversations((prev) => [conv, ...prev]);
      navigate(`/messages/${conv.id}`);
      setShowNewConversation(false);
      // Join the SignalR group so we can receive messages in real-time
      joinConversationGroup(conv.id).catch(() => {});
    },
    [navigate]
  );

  const handleAcceptRequest = useCallback(async (contactId: string) => {
    try {
      await acceptContact(contactId);
      setPendingRequests((prev) => prev.filter((r) => r.id !== contactId));
      // Refresh conversations — a new DM may have appeared
      fetchConversations().then(setConversations).catch(() => {});
    } catch (err) {
      console.error('[MessagesPage] Accept failed:', err);
    }
  }, []);

  const handleRejectRequest = useCallback(async (contactId: string) => {
    try {
      await rejectContact(contactId);
      setPendingRequests((prev) => prev.filter((r) => r.id !== contactId));
      // DEV-1485 — the Sent section's Cancel routes through this same handler
      // (cancelling your own outgoing request IS rejecting that row). Drop it
      // from the sent list too, or the cancelled row lingers on screen until
      // the next full load.
      setSentRequests((prev) => prev.filter((r) => r.id !== contactId));
    } catch (err) {
      console.error('[MessagesPage] Reject failed:', err);
    }
  }, []);

  const handleStartChatWithContact = useCallback(async (userGuid: string) => {
    setChatError(null);
    try {
      // Check if a conversation already exists with this user (in local state)
      const existing = conversations.find((c) =>
        c.conversationType === 'direct' &&
        c.participants.some((p) => p.userGuid.toUpperCase() === userGuid.toUpperCase())
      );
      if (existing) {
        navigate(`/messages/${existing.id}`);
        setSidebarTab('conversations');
        return;
      }
      // Create new direct conversation
      const conv = await createConversation({
        conversationType: 'direct',
        participantGuids: [userGuid],
      });
      setConversations((prev) => [conv, ...prev]);
      navigate(`/messages/${conv.id}`);
      setSidebarTab('conversations');
    } catch (err: any) {
      // If the backend says the conversation already exists, navigate to it
      if (err?.body?.conversationId) {
        const existingId = err.body.conversationId;
        try {
          const existingConv = await fetchConversation(existingId);
          setConversations((prev) => {
            if (prev.some((c) => c.id === existingId)) return prev;
            return [existingConv, ...prev];
          });
          navigate(`/messages/${existingId}`);
          setSidebarTab('conversations');
          return;
        } catch (fetchErr) {
          console.error('[MessagesPage] Failed to fetch existing conversation:', fetchErr);
        }
      }
      console.error('[MessagesPage] Failed to start chat:', err);
      setChatError(err?.message || t('page.chatError'));
    }
  }, [conversations, navigate, t]);

  /* DEV-1240 P3 / DEV-1256 — `?startChatWith=<guid>` query-param entry
   * point was removed. Surfaces that previously navigated to
   * `/messages?startChatWith=…` now dispatch `l4e:open-chat-with` and
   * the ChatTray (bottom-right floating panel) handles the deep-link
   * in-place, keeping the user on whatever page they were on. The
   * legacy MessagesPage URL still works for direct navigation, but no
   * code in the app links to it with the startChatWith param anymore.
   *
   * `searchParams` is still imported in case future entry points need
   * it, but the param-consumption effect is intentionally retired. */

  const handleArchiveConversation = useCallback(async (id: string) => {
    try {
      await archiveConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      // If the archived conversation was active, navigate away
      if (conversationId === id) {
        navigate('/messages');
      }
    } catch (err) {
      console.error('[MessagesPage] Failed to archive conversation:', err);
    }
  }, [conversationId, navigate]);

  const handlePinConversation = useCallback(async (id: string) => {
    try {
      const result = await pinConversation(id);
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, isPinned: result.isPinned } : c))
      );
    } catch (err) {
      console.error('[MessagesPage] Failed to pin conversation:', err);
    }
  }, []);

  // DEV-1391 — re-fetch the active conversation + list after an in-window
  // change (e.g. a group avatar update) so it reflects everywhere immediately.
  const handleConversationUpdated = useCallback(() => {
    if (conversationId) {
      fetchConversation(conversationId).then(setActiveConversation).catch(() => {});
    }
    fetchConversations().then(setConversations).catch(() => {});
  }, [conversationId]);

  /* ---- Unread count for conversations ---- */
  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + c.unreadCount, 0),
    [conversations]
  );

  return (
    <div className={styles.messagesPage}>
      {/* ─── Left panel: sidebar tabs ─────────────────────────────── */}
      <div className={styles.leftPanel}>
        {/* Tab bar */}
        <div className={styles.tabBar}>
          <button
            type="button"
            className={`${styles.tab} ${sidebarTab === 'conversations' ? styles.tabActive : ''}`}
            onClick={() => { setSidebarTab('conversations'); setShowNewConversation(false); }}
          >
            {t('nav.title')}
            {totalUnread > 0 && (
              <span className={styles.tabBadge}>{totalUnread}</span>
            )}
          </button>
          <button
            type="button"
            className={`${styles.tab} ${sidebarTab === 'requests' ? styles.tabActive : ''}`}
            onClick={() => { setSidebarTab('requests'); setShowNewConversation(false); }}
          >
            {t('contacts.title')}
            {pendingRequests.length > 0 && (
              <span className={styles.tabBadge}>{pendingRequests.length}</span>
            )}
          </button>
        </div>

        {/* Tab content */}
        <div className={styles.tabContent}>
          {sidebarTab === 'conversations' && !showNewConversation && (
            <ChatSidebar
              conversations={enrichedConversations}
              activeConversationId={activeConversation?.id}
              currentUserGuid={currentUserGuid}
              onSelectConversation={handleSelectConversation}
              onNewConversation={() => setShowNewConversation(true)}
              onArchiveConversation={handleArchiveConversation}
              onPinConversation={handlePinConversation}
              loading={loading}
              hideTitle
            />
          )}

          {sidebarTab === 'conversations' && showNewConversation && (
            <NewConversationPanel
              users={platformUsers}
              currentUserGuid={currentUserGuid}
              onConversationCreated={handleConversationCreated}
              onCancel={() => setShowNewConversation(false)}
            />
          )}

          {sidebarTab === 'requests' && (
            <ContactRequestsPanel
              requests={pendingRequests}
              sentRequests={sentRequests}
              currentUserGuid={currentUserGuid}
              onAccept={handleAcceptRequest}
              onReject={handleRejectRequest}
              onStartChat={handleStartChatWithContact}
            />
          )}

          {/* Error banner for failed chat actions */}
          {chatError && (
            <div
              className={styles.chatErrorBanner}
              onClick={() => setChatError(null)}
              role="alert"
            >
              {chatError}
            </div>
          )}
        </div>
      </div>

      {/* ─── Center panel: active chat ────────────────────────────── */}
      <div className={styles.centerPanel}>
        {activeConversation ? (
          // DEV-1390 — keyed + animated wrapper so switching conversations
          // animates only the chat window, not the whole page / rail (the
          // page-level slide is now stable across /messages/:id).
          <div key={activeConversation.id} className={styles.chatEnter}>
            <ChatWindow
              conversation={activeConversation}
              currentUserGuid={currentUserGuid}
              onConversationUpdated={handleConversationUpdated}
            />
          </div>
        ) : (
          <div className={styles.noChatSelected}>
            <div className={styles.noChatIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <p className={styles.noChatText}>{t('page.selectConversation')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
